import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TownshipService } from '../../services/township.service';
import { TownshipListResponse } from '../../models/township.models';
import { httpErrorMessage } from '../../core/http-error';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-townships',
  imports: [FormsModule, DatePipe],
  templateUrl: './app-townships.component.html',
  styleUrl: './app-townships.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTownshipsComponent implements OnInit {
  private readonly townshipService = inject(TownshipService);
  private readonly router = inject(Router);

  readonly response = signal<TownshipListResponse | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /** 1-based page index for the UI (the API is 0-based). */
  readonly page = signal(1);
  readonly search = signal('');

  readonly items = computed(() => this.response()?.items ?? []);
  readonly totalCount = computed(() => this.response()?.total_count ?? 0);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / PAGE_SIZE)));
  readonly pageStart = computed(() => (this.items().length ? (this.page() - 1) * PAGE_SIZE + 1 : 0));
  readonly pageEnd = computed(() => (this.page() - 1) * PAGE_SIZE + this.items().length);

  ngOnInit(): void {
    this.fetch();
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages()) return;
    this.page.set(p);
    this.fetch();
  }

  applySearch(): void {
    this.page.set(1);
    this.fetch();
  }

  openTownship(id: string): void {
    this.router.navigate(['/townships', id]);
  }

  private fetch(): void {
    this.loading.set(true);
    this.error.set(null);
    this.townshipService.listTownships(this.page() - 1, PAGE_SIZE, this.search()).subscribe({
      next: res => {
        this.response.set(res);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(httpErrorMessage(err, 'Failed to load townships.'));
        this.loading.set(false);
      },
    });
  }
}
